import type { ProgressSnapshot } from "../progress/session";
import { Icon } from "./Icon";
import { Header, type NavigationProps } from "./SiteHeader";

export function HelpPage({
  progress,
  onNavigate,
  onBack,
}: NavigationProps & {
  readonly progress: ProgressSnapshot;
  readonly onBack: () => void;
}) {
  return (
    <>
      <Header onNavigate={onNavigate} progress={progress} active="help" />
      <main className="panel-page">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Field manual</p>
            <h1>操作与隐私</h1>
            <p className="page-description">
              花一分钟熟悉航线，然后去画出属于你的轨迹。
            </p>
          </div>
          <button className="ghost-button" onClick={onBack}>
            <Icon name="back" />
            返回
          </button>
        </div>
        <div className="help-grid">
          <section className="help-section">
            <div className="section-heading">
              <Icon name="spark" />
              <h2>生长与碰撞</h2>
            </div>
            <p>
              线迹会持续前进，吃下微光就能长大。你可以穿过自己的身体；头部撞上其他线迹、其他头部或场地边界「暮界」会被淘汰。
            </p>
            <p>
              被淘汰的线迹会化为可争夺的微光。每 20
              秒到来的共振潮，会暂时提高沿途自然食物的价值。
            </p>
          </section>
          <section className="help-section help-controls">
            <div className="section-heading">
              <Icon name="settings" />
              <h2>上手操控</h2>
            </div>
            <dl className="control-list">
              <div>
                <dt>指向</dt>
                <dd>移动鼠标，或在触屏航盘上拖动。</dd>
              </div>
              <div>
                <dt>转向</dt>
                <dd>
                  <span className="key-group">
                    <kbd>W</kbd>
                    <kbd>A</kbd>
                    <kbd>S</kbd>
                    <kbd>D</kbd>
                  </span>
                  <span>或</span>
                  <span className="key-group">
                    <kbd>↑</kbd>
                    <kbd>←</kbd>
                    <kbd>↓</kbd>
                    <kbd>→</kbd>
                  </span>
                </dd>
              </div>
              <div>
                <dt>加速</dt>
                <dd>
                  <kbd>Space</kbd>
                  <span>或</span>
                  <kbd>Shift</kbd>
                </dd>
              </div>
              <div>
                <dt>暂停</dt>
                <dd>
                  <kbd>Esc</kbd>
                  <span>或</span>
                  <kbd>P</kbd>
                </dd>
              </div>
            </dl>
            <p>
              同时按两个相邻方向可斜向移动。触屏玩家也可按住右侧按钮加速；加速会消耗长度，留下所有线迹都能拾取的碎片。切换页面或应用时，对局会自动暂停。
            </p>
          </section>
          <section className="help-section">
            <div className="section-heading">
              <Icon name="infinity" />
              <h2>两种航程</h2>
            </div>
            <p>
              <strong>Signal Drift · 三分钟竞技</strong>
              <br />
              180 秒，一次生命。在有限的时间里挑战更长的线迹与更高的信号纪录。
            </p>
            <p>
              <strong>Endless Drift · 无尽漫游</strong>
              <br />
              不限时。熄灭后等待 5 秒，以初始长度重新出发，并获得 5
              秒防护；期间你与其他线迹不会因彼此碰撞而淘汰。
            </p>
          </section>
          <section className="help-section">
            <div className="section-heading">
              <Icon name="shield" />
              <h2>公平，来自同一套规则</h2>
            </div>
            <p>
              机器人和玩家使用同一速度、转向、加速消耗与碰撞规则。它们的视野有限，也需要时间做出反应。
            </p>
            <p>
              所有外观只改变颜色、纹理与头标。换一身喜欢的配色，实力仍由你的走位决定。
            </p>
          </section>
          <section className="help-section">
            <div className="section-heading">
              <Icon name="cloud" />
              <h2>进度保存在哪里</h2>
            </div>
            <p>
              Guest Progress
              只存于当前浏览器。清除浏览器数据可能丢失进度，换浏览器或设备也不会自动带走存档。
            </p>
            <p>
              已获云存档授权的个人主页账户，可以在这里登录并同步账户进度。游客进度与账户进度分别保存，未授权账户无法访问云存档。
            </p>
          </section>
          <section className="help-section">
            <div className="section-heading">
              <Icon name="lock" />
              <h2>只保存必要的数据</h2>
            </div>
            <p>
              当前版本不发送玩法分析事件。长期进度只记录你的成绩、累计统计、设置、教学进展与外观。
            </p>
            <p>
              站点使用 Cloudflare Web Analytics
              统计访问与页面加载性能，用于排查访问和速度问题。Cloudflare
              的统计报表可查询最近六个月；我们不另行保存这类统计，也不向其附加账户或存档数据。
            </p>
            <p>
              普通错误信息不会包含账户标识、邮箱或完整存档。本站没有公开注册，也不会为游客创建云端账户。
            </p>
          </section>
          <section className="help-section">
            <div className="section-heading">
              <Icon name="check" />
              <h2>断线后，也能继续</h2>
            </div>
            <p>
              云端暂时不可用时，你仍可在本地游玩。若本地存档损坏，游戏会保留异常数据并以默认进度启动。
            </p>
            <p>
              此浏览器与云端都发生修改时，纪录与已解锁外观会安全合并；设置与当前外观由你选择来源，系统不会静默覆盖。
            </p>
          </section>
        </div>
      </main>
    </>
  );
}
